import { Subject } from 'rxjs';
import { debounceTime, map, tap, takeUntil } from 'rxjs/operators';

import {
  Input,
  Output,
  OnInit,
  OnDestroy,
  Component,
  HostListener,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import { SnTeam, SnCallSign, SnUser } from '@lib/api/entities';
import { SnUsersAPI, SnCallSignsAPI } from '@lib/api/services';

@Component({
  selector: 'sn-call-sign-quick-change',
  templateUrl: './call-sign-quick-change.component.html',
  styleUrls: ['./call-sign-quick-change.component.scss'],
})
export class SnCallSignQuickChangeComponent implements OnInit, OnDestroy {
  @Input()
  public user: SnUser;

  @Input()
  public callSign: string;

  @Input()
  public team: SnTeam;

  @Output()
  public readonly callSignChanged = new EventEmitter<string>();

  @Output()
  public readonly teamChanged = new EventEmitter<SnTeam>();

  public mode: 'view' | 'edit' = 'view';

  public callSignList: SnCallSign[] = [];
  public callSignControl: UntypedFormControl;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _callSignsAPI: SnCallSignsAPI,
    private readonly _usersAPI: SnUsersAPI,
    private readonly _cd: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.callSignControl = new UntypedFormControl('', Validators.required);

    this.callSignControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe((value) => {
        this._loadCallSigns(value);
      });
  }

  public ngOnDestroy(): void {
    this.callSignChanged.complete();
    this.teamChanged.complete();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackCallSign(index: number, item: SnCallSign): any {
    return item.id;
  }

  @HostListener('click', ['$event'])
  public click($event: MouseEvent): void {
    $event.stopPropagation();
  }

  @HostListener('document:click')
  public clickOutside(): void {
    if (this.mode === 'edit' && this.callSignControl.enabled) {
      this.toggleMode();
    }
  }

  public toggleMode(): void {
    this.mode = this.mode === 'view' ? 'edit' : 'view';
    this.callSignControl.patchValue(this.callSign);
  }

  public deleteCallSign(): void {
    const targetCallSign = this.callSignList.filter((c) => c.name === this.callSign);

    if (targetCallSign.length) {
      this._callSignsAPI
        .removeCallSign(this.user.id)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: () => {
            this.callSign = null;
            this.callSignChanged.emit(this.callSign);
            this.team = null;
            this.teamChanged.emit(this.team);
            this.mode = 'view';
            this._cd.detectChanges();
          },
        });
    }
  }

  public setCallSign(selectedItem: MatAutocompleteSelectedEvent): void {
    const callSign = selectedItem.option.value;

    if (this.callSign === callSign) {
      this.toggleMode();
    } else {
      this.callSignControl.disable();

      this._usersAPI
        .changeCallSign(this.user.id, callSign)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: ({ team }) => {
            this.callSign = callSign;
            this.callSignChanged.emit(this.callSign);

            this.team = team;
            this.teamChanged.emit(team);

            this.callSignControl.enable();
            this.toggleMode();
          },
          error: () => {
            this.callSignControl.enable();
          },
        });
    }
  }

  private _loadCallSigns(search: string): void {
    this._callSignsAPI
      .getCallSignList({ search })
      .pipe(
        map((response: any) => response.results),
        tap((callSigns) => {
          if (!callSigns.length) {
            this.callSignControl.setErrors({ invalid: true });
          }
        }),
        takeUntil(this._destroy$),
      )
      .subscribe({
        next: (callSigns) => (this.callSignList = callSigns),
      });
  }
}
